import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  BankOutlined,
  DownloadOutlined,
  HistoryOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import SidebarNav from "../sidebar";
import PermissionGate from "../commoncomponent/PermissionGate";
import {
  getFeatureToggles,
  updateFeatureToggle,
  getFeatureToggleHistory,
} from "../../../service/api";

const { Text, Title, Paragraph } = Typography;

const TOGGLE_ICONS = {
  "crypto-deposit":    <DownloadOutlined />,
  "crypto-withdrawal": <UploadOutlined />,
  "inr-deposit":       <BankOutlined style={{ color: "#52c41a" }} />,
  "inr-withdrawal":    <BankOutlined style={{ color: "#fa8c16" }} />,
};

const CHANGE_TAG = {
  ENABLED:  { color: "success", label: "Enabled" },
  DISABLED: { color: "error",   label: "Disabled" },
  UPDATED:  { color: "default", label: "Updated" },
};

const FILTER_OPTIONS = [
  { value: "",                 label: "All toggles" },
  { value: "crypto-deposit",   label: "Crypto Deposit" },
  { value: "crypto-withdrawal",label: "Crypto Withdrawal" },
  { value: "inr-deposit",      label: "INR Deposit" },
  { value: "inr-withdrawal",   label: "INR Withdrawal" },
];

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : "—");

// ─── Edit modal ───────────────────────────────────────────────────────────────
const EditModal = ({ open, toggle, saving, onCancel, onSave }) => {
  const [draft, setDraft] = useState({ enabled: true, disabledMessage: "" });

  useEffect(() => {
    if (open && toggle) {
      setDraft({
        enabled:         toggle.enabled,
        disabledMessage: toggle.disabledMessage ?? "",
      });
    }
  }, [open, toggle]);

  if (!toggle) return null;

  const handleSave = () => onSave({ ...draft, toggleKey: toggle.toggleKey });

  return (
    <Modal
      open={open}
      title={toggle.label}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <PermissionGate key="save" module="feature-toggle" action="update">
          {draft.enabled ? (
            <Button key="save" type="primary" loading={saving} onClick={handleSave}>
              Save
            </Button>
          ) : (
            <Popconfirm
              title={`Disable ${toggle.label}?`}
              description="Users will not be able to use this feature. API calls will also be rejected."
              okText="Yes, disable"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              onConfirm={handleSave}
            >
              <Button key="save" type="primary" danger loading={saving}>
                Save &amp; Disable
              </Button>
            </Popconfirm>
          )}
        </PermissionGate>,
      ]}
    >
      <Space direction="vertical" style={{ width: "100%", marginTop: 8 }} size={16}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Switch
            checked={draft.enabled}
            onChange={(v) => setDraft((d) => ({ ...d, enabled: v }))}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
          <Text type={draft.enabled ? "success" : "danger"}>
            {draft.enabled ? "Feature is ON — users can use it" : "Feature is OFF — all requests will be rejected"}
          </Text>
        </div>

        {!draft.enabled && (
          <div>
            <Text strong>Disabled Message</Text>
            <Paragraph type="secondary" style={{ marginBottom: 6, fontSize: 12 }}>
              This message is shown to users when they try to use this feature.
            </Paragraph>
            <Input.TextArea
              rows={3}
              maxLength={500}
              showCount
              value={draft.disabledMessage}
              onChange={(e) => setDraft((d) => ({ ...d, disabledMessage: e.target.value }))}
              placeholder="e.g. INR deposits are temporarily paused for maintenance. Please try again in a few hours."
            />
          </div>
        )}
      </Space>
    </Modal>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const FeatureToggle = () => {
  const [toggles, setToggles]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [saving, setSaving]           = useState(false);

  const [history, setHistory]         = useState([]);
  const [histTotal, setHistTotal]     = useState(0);
  const [histPage, setHistPage]       = useState(1);
  const [histFilter, setHistFilter]   = useState("");
  const [histLoading, setHistLoading] = useState(false);

  // ── Load toggles ────────────────────────────────────────────────────────────
  const loadToggles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFeatureToggles();
      setToggles(res?.data?.toggles ?? []);
    } catch {
      toast.error("Failed to load feature toggles");
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load history ─────────────────────────────────────────────────────────────
  const loadHistory = useCallback(async (page = 1, toggleKey = "") => {
    setHistLoading(true);
    try {
      const params = { page, limit: 15 };
      if (toggleKey) params.toggleKey = toggleKey;
      const res = await getFeatureToggleHistory(params);
      setHistory(res?.data?.rows ?? []);
      setHistTotal(res?.data?.total ?? 0);
      setHistPage(page);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setHistLoading(false);
    }
  }, []);

  useEffect(() => { loadToggles(); }, [loadToggles]);
  useEffect(() => { loadHistory(1, histFilter); }, [loadHistory, histFilter]);

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async ({ toggleKey, enabled, disabledMessage }) => {
    setSaving(true);
    try {
      await updateFeatureToggle({ toggleKey, enabled, disabledMessage });
      toast.success("Feature toggle updated");
      setEditTarget(null);
      loadToggles();
      loadHistory(1, histFilter);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update toggle");
    } finally {
      setSaving(false);
    }
  };

  // ── History table columns ────────────────────────────────────────────────────
  const histCols = [
    {
      title: "When",
      dataIndex: "createdAt",
      render: fmtDateTime,
      width: 180,
    },
    {
      title: "Feature",
      dataIndex: "toggleKey",
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: "Change",
      dataIndex: "changeType",
      render: (v) => {
        const t = CHANGE_TAG[v] || CHANGE_TAG.UPDATED;
        return <Badge status={t.color} text={t.label} />;
      },
    },
    {
      title: "Message",
      dataIndex: "disabledMessage",
      render: (v) => <Text type="secondary" style={{ fontSize: 12 }}>{v || "—"}</Text>,
    },
    {
      title: "By",
      dataIndex: "updatedByEmail",
      render: (v) => v || "—",
    },
  ];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid" style={{ padding: 24 }}>
          <Title level={3} style={{ marginBottom: 4 }}>
            Deposit &amp; Withdrawal Controls
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 24 }}>
            Enable or disable INR and crypto deposit/withdrawal globally. Disabled features are rejected at the
            API level — users cannot bypass this through the mobile app or direct API calls.
          </Paragraph>

          <Alert
            type="warning"
            showIcon
            message="These toggles affect all users immediately."
            description="Disabling a feature rejects any new request at the backend, regardless of the client. Existing in-flight transactions are not affected."
            style={{ marginBottom: 24 }}
          />

          {/* ── Toggle cards ──────────────────────────────────────────────── */}
          <Spin spinning={loading}>
            <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
              {toggles.map((t) => (
                <Col key={t.toggleKey} xs={24} sm={12} xl={6}>
                  <Card
                    style={{
                      borderColor: t.enabled ? "#b7eb8f" : "#ffa39e",
                      background:  t.enabled ? "#f6ffed" : "#fff1f0",
                    }}
                    styles={{ body: { padding: "16px 20px" } }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <Space>
                        <span style={{ fontSize: 20 }}>{TOGGLE_ICONS[t.toggleKey]}</span>
                        <div>
                          <Text strong style={{ display: "block" }}>{t.label}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>{t.description}</Text>
                        </div>
                      </Space>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 }}>
                      <Badge
                        status={t.enabled ? "success" : "error"}
                        text={
                          <Text style={{ fontSize: 13, fontWeight: 600 }}>
                            {t.enabled ? "Active" : "Disabled"}
                          </Text>
                        }
                      />
                      <PermissionGate module="feature-toggle" action="update">
                        <Button size="small" onClick={() => setEditTarget(t)}>
                          Edit
                        </Button>
                      </PermissionGate>
                    </div>

                    {!t.enabled && t.disabledMessage && (
                      <Alert
                        type="error"
                        message={t.disabledMessage}
                        style={{ marginTop: 12, fontSize: 12 }}
                      />
                    )}

                    {t.updatedAt && (
                      <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 8 }}>
                        Last updated: {fmtDateTime(t.updatedAt)}
                        {t.updatedByEmail ? ` by ${t.updatedByEmail}` : ""}
                      </Text>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          </Spin>

          {/* ── History ───────────────────────────────────────────────────── */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <HistoryOutlined />
            <Title level={5} style={{ margin: 0 }}>Change History</Title>
            <Select
              style={{ width: 200 }}
              options={FILTER_OPTIONS}
              value={histFilter}
              onChange={(v) => setHistFilter(v)}
              placeholder="Filter by feature"
            />
          </div>

          <Table
            dataSource={history}
            columns={histCols}
            rowKey="_id"
            loading={histLoading}
            pagination={{
              current:  histPage,
              total:    histTotal,
              pageSize: 15,
              showTotal: (t) => `${t} changes`,
              onChange: (p) => loadHistory(p, histFilter),
            }}
            size="small"
          />
        </div>
      </div>

      {/* Edit modal */}
      <EditModal
        open={!!editTarget}
        toggle={editTarget}
        saving={saving}
        onCancel={() => setEditTarget(null)}
        onSave={handleSave}
      />
    </>
  );
};

export default FeatureToggle;
